import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatSelect, MatOption } from '@angular/material/select';
import { firstValueFrom } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatCheckbox } from '@angular/material/checkbox';
import { ProductTaskBoardTimeBudget } from 'src/app/models/product_task_board_time_budget.model';
import { ProductTaskTimeBudget } from 'src/app/models/product_task_time_budget.model';

const SLUG_TASK_TIME_TRACKING = 'task-time-tracking';
const SLUG_PRODUCT_DESIGN = 'product-design';
const SLUG_REVIEW_TASK = 'review-task';

export interface TaskTimeBudgetDialogData {
  task: {
    id: number;
    product_task_time_budgets: Array<{
      id?: number;
      budgetable_type: string;
      budgetable_id: number;
      time_in_seconds: number;
      budgetable?: { slug?: string };
    }>;
  };
  product_task_board?: {
    id: number;
    product_task_board_time_budgets: Array<{
      id: number;
      budgetable_type: string;
      budgetable_id: number;
      time_in_seconds: number;
      budgetable?: { slug?: string };
    }>;
  };
  company_product_phase_task_time_autocalc_settings: Array<{
    company_product_phase_task_time_autocalc_setting_category: { slug: string; working_time_category?: { id: number } };
  }>;
}

enum TimeBudgetModeEnum {
  Autocalc = 'autocalc',
  Manual = 'manual',
}
enum TimeBudgetCategoryEnum {
  Dev = 'dev',
  UxUi = 'uxui',
  Rev = 'rev',
}
@Component({
  selector: 'app-task-time-budget-dialog',
  templateUrl: './task-time-budget-dialog.component.html',
  styleUrl: './task-time-budget-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    MatDialogActions,
    MatButton,
    MatSelect,
    MatOption,
    MatRadioGroup,
    MatRadioButton,
    MatCheckbox,
  ],
})
export class TaskTimeBudgetDialogComponent {
  private readonly api = inject(ApiService);
  private readonly dialogRef = inject(MatDialogRef<TaskTimeBudgetDialogComponent>);
  readonly data = inject<TaskTimeBudgetDialogData>(MAT_DIALOG_DATA);

  readonly TimeBudgetModeEnum = TimeBudgetModeEnum;

  mode: TimeBudgetModeEnum = TimeBudgetModeEnum.Autocalc;
  selectedCategory: TimeBudgetCategoryEnum = TimeBudgetCategoryEnum.Dev;
  timeInput: string = '';
  devChecked: boolean = true;
  uxuiChecked: boolean = false;
  /** Solo aplica cuando ya hay PTTB con tiempo > 0: permite optar por no actualizar el general. */
  alsoApplyToGeneral: boolean = !(this.data.task.product_task_time_budgets ?? []).some(
    (b: { time_in_seconds?: number }) => b.time_in_seconds != null && b.time_in_seconds > 0
  );
  saving: boolean = false;
  errorMessage: string | null = null;

  readonly hasBoard: boolean = this.data.product_task_board != null;
  /** Presupuesto general con tiempo ya asignado; si no hay o todo es 0, el backend propaga al guardar PTBTB. */
  readonly hasGeneralTimeBudgets: boolean = (this.data.task.product_task_time_budgets ?? []).some(
    (b: { time_in_seconds?: number }) => b.time_in_seconds != null && b.time_in_seconds > 0
  );

  close(): void {
    this.dialogRef.close(false);
  }

  /** Parse "3h 20m", "2h", "30m" to seconds (override mode: only positive or zero). Returns null if invalid. */
  static parseTimeToSeconds(input: string): number | null {
    const trimmed = (input || '').trim();
    if (!trimmed) return null;
    const hMatch = trimmed.match(/(\d+)\s*h/);
    const mMatch = trimmed.match(/(\d+)\s*m/);
    const hours = hMatch ? parseInt(hMatch[1], 10) : 0;
    const minutes = mMatch ? parseInt(mMatch[1], 10) : 0;
    if (hours === 0 && minutes === 0 && (hMatch || mMatch)) return 0;
    if (!hMatch && !mMatch) return null;
    return hours * 3600 + minutes * 60;
  }

  private getBudgetableIdForSlug(slug: string): number | null {
    const setting = this.data.company_product_phase_task_time_autocalc_settings?.find(
      s => s.company_product_phase_task_time_autocalc_setting_category?.slug === slug
    );
    return setting?.company_product_phase_task_time_autocalc_setting_category?.working_time_category?.id ?? null;
  }

  private getCurrentBoardBudgetBySlug(slug: string): { id: number; time_in_seconds: number } | undefined {
    const board = this.data.product_task_board;
    if (!board?.product_task_board_time_budgets) return undefined;
    const b = board.product_task_board_time_budgets.find(x => x.budgetable?.slug === slug);
    return b != null ? { id: b.id, time_in_seconds: b.time_in_seconds ?? 0 } : undefined;
  }

  /** Set board budget for the given slug to the exact time (override, no delta). */
  private async setBoardBudget(slug: string, timeInSeconds: number): Promise<void> {
    if (!this.data.product_task_board) return;

    const boardId = this.data.product_task_board.id;
    const existing = this.getCurrentBoardBudgetBySlug(slug);
    const wtcId = this.getBudgetableIdForSlug(slug);
    if (wtcId === null) return;

    const nextSeconds = Math.max(0, Math.round(timeInSeconds));

    if (existing && existing.id > 0) {
      await firstValueFrom(this.api.updateProductTaskBoardTimeBudget(existing.id, { time_in_seconds: nextSeconds }));
    } else {
      await firstValueFrom(
        this.api.createProductTaskBoardTimeBudget({
          product_task_board_id: boardId,
          budgetable_type: 'App\\Models\\WorkingTimeCategory',
          budgetable_id: wtcId,
          time_in_seconds: nextSeconds,
        } as unknown as ProductTaskBoardTimeBudget)
      );
    }
  }

  async submit(): Promise<void> {
    this.errorMessage = null;
    const seconds = TaskTimeBudgetDialogComponent.parseTimeToSeconds(this.timeInput);
    if (seconds === null) {
      this.errorMessage = 'Formato de tiempo no válido. Usa por ejemplo 2h 30m.';
      return;
    }

    if (this.mode === TimeBudgetModeEnum.Manual) {
      if (!this.hasBoard) {
        this.errorMessage = 'En modo manual el tiempo solo se aplica al tablero actual. Abre la tarea desde un tablero.';
        return;
      }
    } else if (seconds < 0) {
      this.errorMessage = 'El tiempo debe ser 0 o positivo.';
      return;
    }

    this.saving = true;
    try {
      const success = await this.applyTimeBudget(seconds);
      if (success) this.dialogRef.close(true);
    } catch (err) {
      this.errorMessage =
        err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Error al guardar.';
    } finally {
      this.saving = false;
    }
  }

  /** Applies the time budget (autocalc or manual). Returns false if validation failed (e.g. no distribution); true on success. */
  private async applyTimeBudget(seconds: number): Promise<boolean> {
    if (this.mode === TimeBudgetModeEnum.Autocalc) {
      const totalSeconds = Math.max(0, seconds);
      const distribution = await firstValueFrom(
        this.api.autocalcTaskTime(this.data.task.id, {
          time_in_seconds: totalSeconds,
          needs_uxui: this.uxuiChecked ? 1 : 0,
          needs_review: 1,
          needs_task_time_tracking: this.devChecked ? 1 : 0,
        })
      );
      if (!distribution) {
        this.errorMessage = 'No se pudo calcular la distribución.';
        return false;
      }
      const devSec = this.devChecked ? (distribution[SLUG_TASK_TIME_TRACKING] ?? 0) : 0;
      const uxuiSec = this.uxuiChecked ? (distribution[SLUG_PRODUCT_DESIGN] ?? 0) : 0;
      const revSec = distribution[SLUG_REVIEW_TASK] ?? 0;
      if (this.devChecked) {
        await this.setBoardBudget(SLUG_TASK_TIME_TRACKING, devSec);
      }
      if (this.uxuiChecked) {
        await this.setBoardBudget(SLUG_PRODUCT_DESIGN, uxuiSec);
      }
      await this.setBoardBudget(SLUG_REVIEW_TASK, revSec);
      if (this.hasGeneralTimeBudgets && this.alsoApplyToGeneral) {
        const payload: Array<{ budgetable_type: string; budgetable_id: number; time_in_seconds: number }> = [];
        const enabledSlugs = [SLUG_REVIEW_TASK] as string[];
        if (this.devChecked) enabledSlugs.push(SLUG_TASK_TIME_TRACKING);
        if (this.uxuiChecked) enabledSlugs.push(SLUG_PRODUCT_DESIGN);
        for (const slug of enabledSlugs) {
          const wtcId = this.getBudgetableIdForSlug(slug);
          if (wtcId === null) continue;
          const time_in_seconds =
            slug === SLUG_TASK_TIME_TRACKING ? Math.round(devSec) : slug === SLUG_PRODUCT_DESIGN ? Math.round(uxuiSec) : Math.round(revSec);
          payload.push({ budgetable_type: 'App\\Models\\WorkingTimeCategory', budgetable_id: wtcId, time_in_seconds });
        }
        if (payload.length > 0) {
          await firstValueFrom(this.api.updateProductTaskTimeBudgets(this.data.task.id, payload as unknown as ProductTaskTimeBudget[]));
        }
      }
      return true;
    }
    const slug = this.slugForManualCategory(this.selectedCategory);
    await this.setBoardBudget(slug, seconds);
    return true;
  }

  private slugForManualCategory(cat: TimeBudgetCategoryEnum): string {
    const categoryToSlug: Record<TimeBudgetCategoryEnum, string> = {
      [TimeBudgetCategoryEnum.Dev]: SLUG_TASK_TIME_TRACKING,
      [TimeBudgetCategoryEnum.UxUi]: SLUG_PRODUCT_DESIGN,
      [TimeBudgetCategoryEnum.Rev]: SLUG_REVIEW_TASK,
    };
    return categoryToSlug[cat];
  }
}
